// Simulated trend data from Trend Agent - with multi-category support
export const trendData = [
  {
    id: 1,
    macro: 'Health & Wellness',
    micro: 'Functional Food & Drink',
    signal: 'Mushroom Coffee',
    velocity: 'High',
    sources: 47,
    trendScore: 8.2,
    status: 'new',
    categories: ['Health & Wellness', 'Food & Drink'], // Multi-category
    externalSignals: {
      searchVolume: '+340% YoY',
      socialMentions: '12.4K/week',
      retailExpansion: 'Walmart, Target added SKUs Q4',
      investmentActivity: '$45M Series B (Ryze)',
    },
    deepDive: {
      summary: 'Adaptogenic mushroom coffee blends are experiencing breakout growth, driven by health-conscious consumers seeking cognitive benefits without traditional caffeine crashes. The category has evolved from specialty/wellness niche to mainstream grocery.',
      audienceOverlap: '34% overlap with existing wellness vertical readers',
      growthPotential: 'High - 18-44 demo indexing 2.3x above baseline',
      competitorCoverage: 'Moderate - major outlets covering sporadically',
      consumerIntent: 'Research-heavy, high purchase intent signals',
    }
  },
  {
    id: 2,
    macro: 'Health & Wellness',
    micro: 'Functional Food & Drink',
    signal: 'Gut Health Sodas',
    velocity: 'Medium',
    sources: 31,
    trendScore: 7.1,
    status: 'new',
    categories: ['Health & Wellness', 'Food & Drink', 'Lifestyle'], // Multi-category
    externalSignals: {
      searchVolume: '+180% YoY',
      socialMentions: '8.2K/week',
      retailExpansion: 'Olipop, Poppi mainstream distribution',
      investmentActivity: '$30M raised collectively',
    },
    deepDive: {
      summary: 'Prebiotic and probiotic sodas positioned as healthier alternatives to traditional soft drinks. Category growing rapidly but competitive landscape increasingly crowded.',
      audienceOverlap: '28% overlap with lifestyle readers',
      growthPotential: 'Medium - saturating market',
      competitorCoverage: 'High - well-covered category',
      consumerIntent: 'Brand comparison, price sensitivity',
    }
  },
  {
    id: 3,
    macro: 'Health & Wellness',
    micro: 'Sleep Optimization',
    signal: 'Mouth Taping',
    velocity: 'High',
    sources: 29,
    trendScore: 6.8,
    status: 'reviewing',
    categories: ['Health & Wellness'], // Single category
    externalSignals: {
      searchVolume: '+520% YoY',
      socialMentions: '15.1K/week',
      retailExpansion: 'Amazon bestseller lists',
      investmentActivity: 'Limited - low barrier products',
    },
    deepDive: {
      summary: 'Viral sleep hack with mixed scientific backing. High engagement potential but requires careful editorial framing around health claims.',
      audienceOverlap: '22% overlap',
      growthPotential: 'Medium - fad risk',
      competitorCoverage: 'Low - underserved topic',
      consumerIntent: 'How-to, safety concerns',
    }
  }
];

// Simulated content library results
export const contentLibraryResults = {
  existingContent: [
    { title: '10 Coffee Alternatives for Better Energy', performance: 'High', age: '8 months', pageviews: '145K' },
    { title: 'Adaptogens Explained: What Actually Works', performance: 'Medium', age: '14 months', pageviews: '67K' },
    { title: 'The Wellness Drink Trends Taking Over TikTok', performance: 'High', age: '3 months', pageviews: '203K' },
  ],
  gapAnalysis: {
    coverage: 'Partial',
    gaps: [
      'No dedicated mushroom coffee buyer guide',
      'Missing brand comparison content',
      'No recipe/preparation guides',
      'Limited SEO-optimized product reviews'
    ],
    opportunity: 'High - clear content gaps in high-intent search areas'
  }
};

// Simulated revenue/performance data
export const revenueData = {
  projectedRPM: '$18.40',
  commercePotential: 'High',
  affiliateOpportunity: 'Amazon, brand direct programs available',
  historicalPerformance: {
    similarContent: '$14.20 avg RPM',
    wellnessVertical: '$12.80 avg RPM',
    topPerformer: '$31.50 RPM (supplement guides)'
  },
  audienceMatch: {
    score: 82,
    breakdown: {
      demographics: 78,
      interests: 88,
      behavioralFit: 81
    }
  }
};

// MAIA Agent Configuration
export const maiaAgents = [
  { id: 'grok', name: 'Grok', icon: '\u{1F50D}', color: '#1da1f2' },
  { id: 'openai', name: 'OpenAI', icon: '\u25D0', color: '#10a37f' },
  { id: 'claude', name: 'Claude', icon: '\u25C9', color: '#cc785c' },
  { id: 'mcclatchy-news', name: 'McClatchy News', icon: '\u{1F4F0}', color: '#6366f1' },
  { id: 'mcclatchy-le', name: 'McClatchy L&E', icon: '\u{1F4CA}', color: '#8b5cf6' },
  { id: 'trendhunter', name: 'Trend Hunter GPT', icon: '\u{1F4C8}', color: '#10b981' },
  { id: 'direct', name: 'Answer Directly', icon: '\u{1F464}', color: '#6b7280' },
];

export const workflowSteps = [
  { id: 0, name: 'Trend Intake', description: 'Review incoming signals from Trend Agent' },
  { id: 1, name: 'Deep Dive', description: 'Execute detailed analysis on selected trend' },
  { id: 2, name: 'Content Match', description: 'Search content library + gap analysis' },
  { id: 3, name: 'Scoring', description: 'Apply revenue and audience scoring' },
  { id: 4, name: 'Decision', description: 'HITL approval gate' },
  { id: 5, name: 'Brief Creation', description: 'Package opportunity for Content Engine' },
];

// Simulated MAIA response
export const simulatedMaiaResponse = {
  agents: ['Claude', 'Trend Hunter GPT', 'OpenAI'],
  synthesizedInsight: `**Trend Validation: CONFIRMED - High Opportunity**

The mushroom coffee trend represents a legitimate category expansion within functional beverages, not a fleeting fad. Key validation points:

**Consumer Pain Points Solved:**
- Caffeine sensitivity without sacrificing ritual
- Cognitive enhancement seeking (focus, clarity)
- Immune support integration into daily habits
- "Better for you" substitution psychology

**12-18 Month Trajectory:**
Expect mainstream grocery penetration to accelerate. Current trajectory suggests 40-60% category growth through 2026. Key inflection point: Starbucks or major chain adoption would signal mass market arrival.

**Key Players to Watch:**
- Four Sigmatic (pioneer, premium positioning)
- RYZE (aggressive DTC growth, $45M raise)
- Everyday Dose (younger demo targeting)
- MUD\\WTR (adjacent competitor, caffeine-free)

**Content Angle Recommendations:**
1. "Best Mushroom Coffee" buyer's guide (high commercial intent)
2. Taste comparison vs. regular coffee (addresses skepticism)
3. Science explainer: Do adaptogens actually work? (builds authority)
4. Brand breakdown with price/value analysis

**Counter-Signals & Risks:**
- FDA regulatory scrutiny on health claims possible
- Taste barrier remains for mainstream adoption
- Market saturation risk if major CPG brands enter
- Some clinical evidence still limited`,
  sources: [
    { name: 'Trend Hunter Database', relevance: 'Category growth data' },
    { name: 'Consumer Search Analysis', relevance: 'Intent patterns' },
    { name: 'Investment tracking', relevance: 'Funding signals' },
  ]
};
